import { useCallback, useMemo, useRef, useState } from 'react';
export function useFileUploadProgress(items) {
    return useMemo(() => {
        const progress = {
            success: 0,
            error: 0,
            uploading: 0,
            pending: 0,
            incomplete: 0,
            total: items.length
        };
        return items.reduce((progress, item) => {
            progress[item.status]++;
            return progress;
        }, progress);
    }, [items]);
}
export function useFileUploadManager(props = {}) {
    const { unique = true, includeHiddenFiles = false, includeNestedFiles = false } = props;
    const idRef = useRef(0);
    const [items, setItems] = useState([]);
    const progress = useFileUploadProgress(items);
    const files = useMemo(() => {
        return items.map(({ file }) => file);
    }, [items]);
    const add = useCallback((files) => {
        files = files.filter(file => {
            const path = file.webkitRelativePath || '';
            // exclude hidden files
            if (!includeHiddenFiles && file.name.startsWith('.')) {
                return false;
            }
            // exclude files from subdirectories
            return includeNestedFiles || !path || path.split('/').length <= 2;
        });
        const newItems = files.map(file => {
            const fileName = `${file.name}-${file.size}`;
            const id = !unique
                ? `file-${fileName}`
                : `file-${idRef.current++}-${fileName}`;
            return { id, file, status: 'pending' };
        });
        setItems(items => {
            const copy = Array.from(items);
            const filteredItems = newItems.filter(newItem => {
                // filter out existing ids
                const index = copy.findIndex(item => item.id === newItem.id);
                // if reuploading the same file, update the status back to pending
                const existingItem = copy[index];
                if (existingItem) {
                    copy.splice(index, 1, Object.assign(Object.assign({}, existingItem), { status: 'pending' }));
                }
                return !existingItem;
            });
            return copy.concat(filteredItems);
        });
    }, [includeHiddenFiles, includeNestedFiles, unique]);
    const remove = useCallback((id) => {
        setItems(items => {
            const index = items.findIndex(file => file.id === id);
            if (index < 0) {
                return items;
            }
            const copy = Array.from(items);
            copy.splice(index, 1);
            return copy;
        });
    }, []);
    const clear = useCallback(() => {
        idRef.current = 0;
        setItems([]);
    }, []);
    const setStatus = useCallback((id, status) => {
        setItems(items => {
            const index = items.findIndex(item => item.id === id);
            if (index < 0) {
                return items;
            }
            const copy = Array.from(items);
            const updated = Object.assign(Object.assign({}, copy[index]), { status });
            copy.splice(index, 1, updated);
            return copy;
        });
    }, []);
    const manager = useMemo(() => {
        return { items, files, progress, add, remove, clear, setStatus };
    }, [items, files, progress, add, remove, clear, setStatus]);
    return manager;
}
//# sourceMappingURL=useFileUploadManager.js.map